<template>
    <div>
        <div id="place-form" v-if="!user.global_admin">
            <h1>{{ t('places-access_denied') }}</h1>
        </div>
        <div id="place-form" v-if="user.global_admin">
            <loader v-if="is('loading')"></loader>
            <div class="container-fluid" v-if="is('ready')">
                <h2>{{ editMode ? t('places-edit_place') : t('places-add_place') }}</h2>
                <form @submit.prevent="save">
                    <div class="field">
                        <label>{{ t('places-form-logo') }}</label>
                        <custom-image
                            required
                            v-model="place.logo"
                            :title="t('places-form-logo_title')"
                            @cleanImageError="validateLogo(true)"
                        />
                        <div class="clear"></div>
                        <img
                            :src="logoSrc"
                            class="preview"
                            width="150"
                            v-if="place.logo"
                        />
                        <span v-if="errors.logo" class="error">{{
                            errors.logo
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-name') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.name"
                            @input="clearCustomValidity($event); validateName()"
                            :title="t('places-form-name_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-name_required')"
                        />
                        <span v-if="errors.name" class="error">{{
                            errors.name
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-slug') }}</label>
                        <div class="input-group">
                            <input
                                type="text"
                                required
                                class="form-control"
                                v-model="place.slug"
                                maxlength="20"
                                @input="clearCustomValidity($event); validateSlug()"
                                :title="t('places-form-slug_title')"
                                @invalid.prevent="setCustomValidity($event, 'places-form-validation-slug_required')"
                            />
                            <char-counter
                                :size="place.slug ? 20 - place.slug.length : 20"
                            ></char-counter>
                        </div>
                        <span v-if="errors.slug" class="error">{{
                            errors.slug
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-legal_name') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.legal_name"
                            @input="clearCustomValidity($event); validateLegalName()"
                            :title="t('places-form-legal_name_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-legal_name_required')"
                        />
                        <span v-if="errors.legal_name" class="error">{{
                            errors.legal_name
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-tax_id') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.tax_id"
                            @input="clearCustomValidity($event); validateTaxId()"
                            :title="t('places-form-tax_id_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-tax_id_required')"
                        />
                        <span v-if="errors.tax_id" class="error">{{
                            errors.tax_id
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-email') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.email"
                            @input="clearCustomValidity($event); validateEmail()"
                            :title="t('places-form-email_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-email_required')"
                        />
                        <span v-if="errors.email" class="error">{{
                            errors.email
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-country') }}</label>
                        <select
                            required
                            v-model="place.country_code"
                            class="form-control"
                            :title="t('places-form-country_title')"
                        >
                            <option
                                v-for="countryOption in countryOptions"
                                :key="countryOption.value"
                                :value="countryOption.value"
                            >{{ countryOption.label }}</option>
                        </select>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-currency') }}</label>
                        <select
                            required
                            v-model="place.currency"
                            class="form-control"
                            :title="t('places-form-currency_title')"
                        >
                            <option
                                v-for="currencyOption in currencyOptions"
                                :key="currencyOption.value"
                                :value="currencyOption.value"
                            >{{ currencyOption.label }}</option>
                        </select>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-zip_code') }}</label>
                        <input
                            v-if="isBrazil"
                            type="text"
                            required
                            class="form-control"
                            v-model="place.zip_code"
                            v-mask="'#####-###'"
                            @input="clearCustomValidity($event); validateZipCode()"
                            @blur="fetchAddress"
                            :title="t('places-form-zip_code_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-zip_code_required')"
                        />
                        <input
                            v-else
                            type="text"
                            required
                            class="form-control"
                            v-model="place.zip_code"
                            @input="clearCustomValidity($event); validateZipCode()"
                            :title="t('places-form-zip_code_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-zip_code_required')"
                        />
                        <span v-if="errors.zip_code" class="error">{{
                            errors.zip_code
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-address') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.address_1"
                            @input="clearCustomValidity($event); validateAddress1()"
                            :title="t('places-form-address_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-address_required')"
                        />
                        <span v-if="errors.address_1" class="error">{{
                            errors.address_1
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-address_2') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.address_2"
                            @input="clearCustomValidity($event); validateAddress2()"
                            :title="t('places-form-address_2_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-address_2_required')"
                        />
                        <span v-if="errors.address_2" class="error">{{
                            errors.address_2
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-zone') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.zone"
                            @input="clearCustomValidity($event); validateZone()"
                            :title="t('places-form-zone_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-zone_required')"
                        />
                        <span v-if="errors.zone" class="error">{{
                            errors.zone
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-city') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.city"
                            @input="clearCustomValidity($event); validateCity()"
                            :title="t('places-form-city_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-city_required')"
                        />
                        <span v-if="errors.city" class="error">{{
                            errors.city
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-state') }}</label>
                        <select
                            v-if="isBrazil"
                            required
                            v-model="place.state"
                            class="form-control"
                            :title="t('places-form-state_title')"
                            @input="clearCustomValidity($event); validateState()"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-state_required')"
                        >
                            <option
                                v-for="stateOption in stateOptions"
                                :key="stateOption.value"
                                :value="stateOption.value"
                            >{{ stateOption.label }}</option>
                        </select>
                        <input
                            v-else
                            type="text"
                            required
                            class="form-control"
                            v-model="place.state"
                            @input="clearCustomValidity($event); validateState()"
                            :title="t('places-form-state_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-state_required')"
                        />
                        <span v-if="errors.state" class="error">{{
                            errors.state
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-phone') }}</label>
                        <kv-phone-input
                            :placeholder="t('places-form-phone_placeholder')"
                            :default-country="place.country_code"
                            v-model="place.phone"
                            @input="validatePhone"
                        />
                        <span v-if="errors.phone" class="error">{{
                            errors.phone
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-color') }}</label>
                        <input
                            type="text"
                            required
                            class="form-control"
                            v-model="place.color"
                            :placeholder="t('places-form-color_placeholder')"
                            @input="clearCustomValidity($event); validateColor()"
                            :title="t('places-form-color_title')"
                            @invalid.prevent="setCustomValidity($event, 'places-form-validation-color_required')"
                        />
                        <span v-if="errors.color" class="error">{{
                            errors.color
                        }}</span>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-timezone') }}</label>
                        <select
                            class="form-control"
                            v-model="place.timezone"
                            :title="t('places-form-timezone_title')"
                        >
                            <option
                                v-for="tz in timezoneOptions"
                                :key="tz"
                                :value="tz"
                            >{{ tz }}</option>
                        </select>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-kevi_marketing') }}</label>
                        <select
                            class="form-control"
                            v-model="place.kevi_marketing"
                        >
                            <option :value="true">{{ t('places-form-yes') }}</option>
                            <option :value="false">{{ t('places-form-no') }}</option>
                        </select>
                    </div>

                    <div class="field">
                        <label>{{ t('places-form-kevi_chat') }}</label>
                        <select
                            class="form-control"
                            v-model="place.kevi_chat"
                        >
                            <option :value="true">{{ t('places-form-yes') }}</option>
                            <option :value="false">{{ t('places-form-no') }}</option>
                        </select>
                    </div>

                    <template v-if="!editMode">
                        <div class="field">
                            <label>{{ t('places-form-credits_per_month') }}</label>
                            <input
                                type="number"
                                required
                                class="form-control"
                                v-model="place.credits_per_month"
                                placeholder=""
                                :title="t('places-form-credits_per_month_title')"
                                @input="clearCustomValidity($event); validateCreditsPerMonth()"
                                @invalid.prevent="setCustomValidity($event, 'places-form-validation-credits_per_month_required')"
                            />
                            <span
                                v-if="errors.credits_per_month"
                                class="error"
                                >{{ errors.credits_per_month }}</span
                            >
                        </div>

                        <div class="field">
                            <label>{{ t('places-form-modules') }}</label>
                            <small>{{ t('places-form-modules_helper') }}</small>
                            <select
                                required
                                multiple
                                v-model="place.modules"
                                class="form-control custom-select"
                                @input="clearCustomValidity($event); validateModules()"
                                :title="t('places-form-modules_title')"
                                @invalid.prevent="setCustomValidity($event, 'places-form-validation-modules_required')"
                            >
                                <option :value="null">{{ t('places-form-none') }}</option>
                                <option :value="'all'">{{ t('places-form-all') }}</option>
                                <option
                                    v-for="(text, mod) in placeModules"
                                    :key="mod"
                                    :value="mod"
                                >
                                    {{ text }}
                                </option>
                            </select>
                        </div>

                        <div class="field">
                            <label>{{ t('places-form-copy_automations_from') }}</label>
                            <autocomplete
                                :list="
                                    places.map((pl) => {
                                        return { text: pl.name, id: pl.id };
                                    })
                                "
                                :model="place.copy_from"
                                @selected="($event) => (place.copy_from = $event)"
                            />
                        </div>

                        <div class="field">
                            <label>Plano</label>
                            <select class="form-control" v-model="selectedPlan">
                                <option :value="null" disabled>Selecione um plano</option>
                                <option
                                    v-for="plan in plans"
                                    :key="plan.id"
                                    :value="plan"
                                >
                                    {{ plan.name }}
                                </option>
                            </select>
                            <span v-if="errors.plan" class="error">{{ errors.plan }}</span>
                        </div>

                        <plan-form
                            v-if="selectedPlan"
                            :selected-plan="selectedPlan"
                            @change="(f) => (planForm = f)"
                        />
                    </template>

                    <button type="button" class="btn btn-primary" :disabled="is('loading')" @click="save">
                        {{ t('places-form-save') }}
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>
<script>
import keviApi from "../keviApi";
import PlacesBase from "./places-base";
import Autocomplete from "./../components/autocomplete/autocomplete";
import PlanForm from "./plan-form.vue";
import KvPhoneInput from "../components/kv-phone-input/kv-phone-input.vue";
import { isValidPhone } from "../helpers/phone";

export default {
    template: "#place-form-template",
    extends: PlacesBase,
    components: { Autocomplete, PlanForm, KvPhoneInput },
    props: {
        user: Object,
    },
    mounted: async function () {
        const placeId = this.$route.params.id;
        if (placeId) {
            this.editMode = true;
            await this.loadPlaceData(placeId);
        } else {
            await this.loadPlans();
        }
    },
    data: function () {
        return {
            state: "ready",
            editMode: false,
            suppressCurrencyAutoMap: false,
            users: [],
            twilio: {
                sid: null,
                token: null,
                numbers: null,
            },
            iagente: {
                username: null,
                password: null,
            },
            placeModules: {},
            place: {
                id: null,
                logo: null,
                name: null,
                legal_name: null,
                tax_id: null,
                email: null,
                address_1: null,
                address_2: null,
                zone: null,
                city: null,
                state: null,
                zip_code: null,
                country_code: "BR",
                locale: "pt-BR",
                currency: "BRL",
                phone: null,
                color: null,
                slug: null,
                timezone: "America/Sao_Paulo",
                uses_activator_flow: "0",
                sms_sender: "kevi",
                copy_from: null,
                credits_per_month: null,
                modules: [],
                kevi_marketing: true,
                kevi_chat: false,
            },
            selectedPlan: null,
            plans: [],
            planForm: {},
            errors: {
                plan: null,
                logo: null,
                name: null,
                legal_name: null,
                tax_id: null,
                email: null,
                address_1: null,
                address_2: null,
                zone: null,
                city: null,
                state: null,
                zip_code: null,
                phone: null,
                color: null,
                slug: null,
                credits_per_month: null,
                modules: null,
            },
        };
    },
    computed: {
        logoSrc: function () {
            return baseUrl + "images/" + this.place.logo;
        },
        timezoneOptions() {
            let zones = [];
            try {
                if (typeof Intl !== "undefined" && Intl.supportedValuesOf) {
                    zones = Intl.supportedValuesOf("timeZone");
                }
            } catch (e) {
                zones = [];
            }
            if (!zones || !zones.length) {
                zones = [
                    "America/Sao_Paulo",
                    "America/Manaus",
                    "America/Rio_Branco",
                    "America/Argentina/Buenos_Aires",
                    "America/Montevideo",
                    "America/Asuncion",
                    "America/Santiago",
                    "America/Bogota",
                    "America/Lima",
                    "America/Mexico_City",
                    "America/New_York",
                    "America/Chicago",
                    "America/Denver",
                    "America/Los_Angeles",
                    "Europe/Lisbon",
                    "Europe/Madrid",
                    "Europe/Paris",
                    "Europe/London",
                    "UTC",
                ];
            }
            const current = this.place.timezone;
            if (current && zones.indexOf(current) === -1) {
                zones = [current, ...zones];
            }
            return zones;
        },
        isBrazil() {
            return this.place.country_code === "BR";
        },
        countryOptions() {
            return [
                "BR",
                "US",
                "CA",
                "AR",
                "UY",
                "PY",
                "CL",
                "CO",
                "PE",
                "MX",
                "PT",
                "ES",
                "FR",
                "DE",
                "IT",
                "GB",
            ].map((value) => ({
                value,
                label: this.t(`places-form-countries-${value}`),
            }));
        },
        currencyOptions() {
            return [
                "BRL",
                "USD",
                "CAD",
                "EUR",
                "ARS",
                "UYU",
                "PYG",
                "CLP",
                "COP",
                "PEN",
                "MXN",
                "GBP",
            ].map((value) => ({
                value,
                label: `${value} - ${this.t(`places-form-currencies-${value}`)}`,
            }));
        },
        stateOptions() {
            return [
                "AC",
                "AL",
                "AP",
                "AM",
                "BA",
                "CE",
                "DF",
                "ES",
                "GO",
                "MA",
                "MT",
                "MS",
                "MG",
                "PA",
                "PB",
                "PR",
                "PE",
                "PI",
                "RJ",
                "RN",
                "RS",
                "RO",
                "RR",
                "SC",
                "SP",
                "SE",
                "TO",
            ].map((value) => ({
                value,
                label: this.t(`places-form-states-${value}`),
            }));
        },
        data: function () {
            let data = { ...this.place };
            data.sms_sender_options = null;
            return data;
        },
    },
    methods: {
        clearCustomValidity(event) {
            event.target.setCustomValidity("");
        },
        setCustomValidity(event, key) {
            event.target.setCustomValidity(this.t(key));
        },
        async loadPlans() {
            let options = keviApi.getOptions("plans", "GET");
            const response = await this.$http(options);
            this.plans = response.body.plans;
        },
        validatePlan() {
            if (!this.selectedPlan) {
                this.errors.plan = this.t("places-form-validation-plan_required");
            } else {
                this.errors.plan = null;
            }
        },
        validateLogo(clean = false) {
            if (!this.place.logo && !clean) {
                this.errors.logo = this.t("places-form-validation-logo_required");
            } else {
                this.errors.logo = null;
            }
        },
        validateLegalName() {
            if (!this.place.legal_name) {
                this.errors.legal_name = this.t("places-form-validation-legal_name_required");
            } else {
                this.errors.legal_name = null;
            }
        },
        validateTaxId() {
            if (!this.place.tax_id) {
                this.errors.tax_id = this.t("places-form-validation-tax_id_required");
            } else {
                this.errors.tax_id = null;
            }
        },
        validateEmail() {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!this.place.email) {
                this.errors.email = this.t("places-form-validation-email_required");
            } else if (!emailPattern.test(this.place.email)) {
                this.errors.email = this.t("places-form-validation-email_format");
            } else {
                this.errors.email = null;
            }
        },
        validateName() {
            if (!this.place.name) {
                this.errors.name = this.t("places-form-validation-name_required");
            } else {
                this.errors.name = null;
            }
        },
        validateSlug() {
            const slug = this.place.slug;

            const slugPattern = /^[-0-9a-z]{0,20}$/;

            if (!this.place.slug) {
                this.errors.slug = this.t("places-form-validation-slug_required");
            } else if (!slugPattern.test(slug)) {
                this.errors.slug = this.t("places-form-validation-slug_format");
            } else {
                this.errors.slug = null;
            }
        },
        validateAddress1() {
            if (!this.place.address_1) {
                this.errors.address_1 = this.t("places-form-validation-address_required");
            } else {
                this.errors.address_1 = null;
            }
        },
        validateAddress2() {
            if (!this.place.address_2) {
                this.errors.address_2 = this.t("places-form-validation-address_2_required");
            } else {
                this.errors.address_2 = null;
            }
        },
        validateZone() {
            if (!this.place.zone) {
                this.errors.zone = this.t("places-form-validation-zone_required");
            } else {
                this.errors.zone = null;
            }
        },
        validateCity() {
            if (!this.place.city) {
                this.errors.city = this.t("places-form-validation-city_required");
            } else {
                this.errors.city = null;
            }
        },
        validateState() {
            if (!this.place.state) {
                this.errors.state = this.t("places-form-validation-state_required");
            } else {
                this.errors.state = null;
            }
        },
        validateZipCode() {
            if (!this.place.zip_code) {
                this.errors.zip_code = this.t("places-form-validation-zip_code_required");
            } else {
                this.errors.zip_code = null;
                if (!this.isBrazil) {
                    return;
                }
                const digits = this.place.zip_code.replace(/\D/g, "");
                if (digits.length === 8) {
                    this.fetchAddress();
                }
            }
        },
        async fetchAddress() {
            if (!this.isBrazil) return;
            const cep = this.place.zip_code ? this.place.zip_code.replace(/\D/g, "") : "";
            if (cep.length !== 8) return;

            try {
                const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
                const data = await response.json();

                if (data.erro) {
                    this.errors.zip_code = "CEP não encontrado.";
                    return;
                }

                if (data.logradouro) this.place.address_1 = data.logradouro;
                if (data.bairro) this.place.zone = data.bairro;
                if (data.localidade) this.place.city = data.localidade;
                if (data.uf) this.place.state = data.uf;

                this.errors.zip_code = null;
                
                this.validateAddress1();
                this.validateZone();
                this.validateCity();
                this.validateState();

            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        },
        validatePhone() {
            if (!this.place.phone) {
                this.errors.phone = this.t("places-form-validation-phone_required");
            } else if (!isValidPhone(this.place.phone, this.place.country_code)) {
                this.errors.phone = this.t("places-form-validation-phone_format");
            } else {
                this.errors.phone = null;
            }
        },
        validateColor() {
            const colorPattern = /^[A-Fa-f0-9]{6}$/;
            if (!this.place.color) {
                this.errors.color = this.t("places-form-validation-color_required");
            } else if (!colorPattern.test(this.place.color)) {
                this.errors.color = this.t("places-form-validation-color_format");
            } else {
                this.errors.color = null;
            }
        },
        validateCreditsPerMonth() {
            if (!this.place.credits_per_month) {
                this.errors.credits_per_month = this.t(
                    "places-form-validation-credits_per_month_required",
                );
            } else if (
                isNaN(this.place.credits_per_month) ||
                this.place.credits_per_month < 0
            ) {
                this.errors.credits_per_month = this.t(
                    "places-form-validation-credits_per_month_positive",
                );
            } else {
                this.errors.credits_per_month = null;
            }
        },
        validateModules() {
            if (!this.place.modules.length) {
                this.errors.modules = this.t("places-form-validation-modules_required");
            } else {
                this.errors.modules = null;
            }
        },
        setState: function (state) {
            this.state = state;
        },
        is: function (state) {
            return state === this.state;
        },
        validateAll() {
            this.validateName();
            this.validateSlug();
            this.validateAddress1();
            this.validateAddress2();
            this.validateZone();
            this.validateCity();
            this.validateState();
            this.validateZipCode();
            this.validatePhone();
            this.validateColor();
            this.validateLogo();
            this.validateLegalName();
            this.validateTaxId();
            this.validateEmail();

            if (!this.editMode) {
                this.validatePlan();
                this.validateCreditsPerMonth();
                this.validateModules();
            }

            return !this.hasErrors();
        },
        hasErrors() {
            return (
                Object.values(this.errors).some(error => error !== null)
            );
        },
        async loadPlaceData(placeId) {
            this.setState("loading");
            this.suppressCurrencyAutoMap = true;
            try {
                const response = await this.$http(
                    keviApi.getOptions("place/" + placeId + "/details")
                );
                const data = response.body;
                this.place.id = data.id;
                this.place.name = data.name;
                this.place.legal_name = data.legal_name;
                this.place.tax_id = data.tax_id;
                this.place.email = data.email;
                this.place.phone = data.phone;
                this.place.address_1 = data.address_1;
                this.place.address_2 = data.address_2;
                this.place.zone = data.zone;
                this.place.city = data.city;
                this.place.state = data.state;
                this.place.zip_code = data.zip_code;
                this.place.country_code = data.country_code || "BR";
                this.place.locale = data.locale || this.localeForCountry(this.place.country_code);
                this.place.currency = data.currency || "BRL";
                this.place.color = data.color;
                this.place.slug = data.slug;
                this.place.logo = data.logo;
                this.place.timezone = data.timezone || "America/Sao_Paulo";
                this.place.kevi_marketing = data.kevi_marketing !== undefined ? Boolean(data.kevi_marketing) : true;
                this.place.kevi_chat = data.kevi_chat !== undefined ? Boolean(data.kevi_chat) : false;
                this.setState("ready");
            } catch (_e) {
                this.$store.dispatch("notification/warning", {
                    text: this.t("places-form-notification-load_error"),
                    duration: 5000,
                });
                this.setState("ready");
            } finally {
                this.$nextTick(() => {
                    this.suppressCurrencyAutoMap = false;
                });
            }
        },
        save: async function () {
            if (!this.validateAll()) {
                this.$store.dispatch("notification/warning", {
                    text: this.t("places-form-notification-fill_fields"),
                    duration: 5000,
                });
                return;
            }

            if (!this.is("loading")) {
                this.setState("loading");
                try {
                    if (this.editMode) {
                        await this.$http(
                            keviApi.getOptions("place/" + this.place.id, "PUT", this.data)
                        );
                        this.$store.dispatch("notification/success", {
                            text: this.t("places-form-notification-updated"),
                            duration: 5000,
                        });
                        this.$router.push("/place/" + this.place.id);
                    } else {
                        const response = await this.$http(
                            keviApi.getOptions("places", "POST", this.data)
                        );
                        const placeId = response.body.id;

                        if (this.selectedPlan && this.planForm.plans_id) {
                            try {
                                this.planForm.change_reason = "Plano inicial";
                                await this.$http(
                                    keviApi.getOptions(
                                        "place/" + placeId + "/plan",
                                        "POST",
                                        this.planForm
                                    )
                                );
                            } catch (planError) {
                                console.error("Erro ao vincular plano inicial:", planError);
                            }
                        }

                        this.$store.dispatch("notification/success", {
                            text: this.t("places-form-notification-saved"),
                            duration: 5000,
                        });
                        this.$router.push("/place/" + placeId);
                    }
                } catch (_e) {
                    const errorText = this.editMode
                        ? this.t("places-form-notification-update_error")
                        : this.t("places-form-notification-fill_fields");
                    this.$store.dispatch("notification/warning", {
                        text: errorText,
                        duration: 5000,
                    });
                    this.state = "ready";
                }
            }
        },
        getModules: function () {
            this.setState("loading");
            let options = keviApi.getOptions("places/modules", "GET");
            this.$http(options).then(
                (response) => {
                    this.placeModules = response.body;
                    this.setState("ready");
                },
                (_response) => {
                    this.setState("ready");
                },
            );
        },
        localeForCountry(countryCode) {
            const countryLocaleMap = {
                BR: "pt-BR",
                US: "en-US",
                CA: "en-CA",
                AR: "es-AR",
                UY: "es-UY",
                PY: "es-PY",
                CL: "es-CL",
                CO: "es-CO",
                PE: "es-PE",
                MX: "es-MX",
                PT: "pt-PT",
                ES: "es-ES",
                FR: "fr-FR",
                DE: "de-DE",
                IT: "it-IT",
                GB: "en-GB",
            };
            return countryLocaleMap[countryCode] || "pt-BR";
        },
    },
    watch: {
        "place.country_code"(newCode) {
            if (this.suppressCurrencyAutoMap) {
                if (newCode !== "BR") {
                    this.errors.state = null;
                    this.errors.zip_code = null;
                }
                return;
            }
            const countryCurrencyMap = {
                BR: "BRL",
                US: "USD",
                CA: "CAD",
                AR: "ARS",
                UY: "UYU",
                PY: "PYG",
                CL: "CLP",
                CO: "COP",
                PE: "PEN",
                MX: "MXN",
                PT: "EUR",
                ES: "EUR",
                FR: "EUR",
                DE: "EUR",
                IT: "EUR",
                GB: "GBP",
            };
            const mappedCurrency = countryCurrencyMap[newCode];
            if (mappedCurrency) {
                this.place.currency = mappedCurrency;
            }
            this.place.locale = this.localeForCountry(newCode);
            if (newCode !== "BR") {
                this.errors.state = null;
                this.errors.zip_code = null;
            }
        },
        "place.copy_from"() {
            if (this.place.copy_from) {
                this.filter_selected_place = this.places.find(
                    (pl) => pl.id === this.place.copy_from,
                );
                return;
            }
            this.filter_selected_place = null;
        },
        "place.modules"() {
            if (this.place.modules.includes("all")) {
                this.place.modules = Object.keys(this.placeModules);
            }
        },
    },
    beforeMount() {
        this.filter_selected_place = null;
        this.place.copy_from = null;
        if (!this.$route.params.id) {
            this.getModules();
        }
    },
};
</script>
